import { useEffect, useRef, useState } from "react";
import { countOnce } from "@/lib/api";
import { Shell, useSiteConfig } from "./Shell";
import { chirp, useQuip } from "./quipState";
import { QuipBoard, QuipKeys, QuipSolved, TraceBanner } from "./QuipParts";

const SOLVED_TEXT = "reply to my email with your face. clear. no one else in the frame.";

export default function CipherPage() {
  const cfg = useSiteConfig();
  const q = useQuip();
  const [sel, setSel] = useState(null);
  const [glow, setGlow] = useState(null);
  const [bad, setBad] = useState(0);
  const [trace, setTrace] = useState("none");  // none → trace → banner → none
  const timers = useRef([]);
  const later = (ms, fn) => timers.current.push(setTimeout(fn, ms));
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => countOnce("quip_visit"), []);

  const firstOpen = (known) => (q.puz ? Array.from(q.puz.cipher).find((c) => q.isLetter(c) && !known[c]) : null);
  const current = sel && !q.known[sel] ? sel : firstOpen(q.known);
  const finished = useRef(false);
  useEffect(() => { if (q.solved && !finished.current) { finished.current = true; q.finish(); } }, [q.solved]);  // eslint-disable-line react-hooks/exhaustive-deps

  const press = async (k) => {
    if (trace !== "none" || !current) return;
    const c = current;
    const r = await q.guess(c, k);
    if (r === "right") { chirp(false); setGlow(c); later(900, () => setGlow(null)); setSel(null); }
    if (r === "wrong" || r === "trace") { chirp(true); setBad((n) => n + 1); }
    if (r === "trace") {
      setTrace("trace");
      later(1600, () => setTrace("banner"));
      later(3600, async () => { await q.wipe(); setSel(null); setTrace("none"); });
    }
  };
  const pressRef = useRef(press);
  pressRef.current = press;
  useEffect(() => {
    const key = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || !/^[a-z]$/i.test(e.key)) return;
      e.preventDefault();
      pressRef.current(e.key.toUpperCase());
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);

  const used = new Set(Object.values(q.known));
  return (
    <Shell testid="cipher-page">
      <div className="crt quip flex flex-1 flex-col" data-testid="quip-crt">
        <TraceBanner phase={trace} />
        <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-3 pt-8 sm:px-10 sm:pt-12">
          <div className="mb-8 flex items-center justify-between px-1 text-[10px] tracking-[0.4em] text-[color:var(--w1-muted)]">
            <span>333SECRET // DECODE</span>
            {!q.solved && <span key={bad} data-testid="quip-trace-counter" className={`${q.strikes ? "text-[color:var(--w1-red)]" : ""} ${bad ? "quip-bad" : ""}`}>TRACE {q.strikes}/3</span>}
          </div>
          {q.error && <p data-testid="quip-error" className="text-center text-xs tracking-[0.3em] text-[color:var(--w1-red)]">NO SIGNAL</p>}
          {q.puz && (
            <>
              <div key={`b${bad}`} className={bad ? "quip-bad" : ""}>
                <QuipBoard cipher={q.puz.cipher} known={q.known} selected={current} glow={glow} onSelect={setSel} solved={q.solved} />
              </div>
              <p data-testid="quip-clue" className="mt-8 text-center text-[11px] tracking-[0.35em] text-[color:var(--w1-muted)]">
                CLUE: {q.puz.clue.cipher} = {q.puz.clue.plain}
              </p>
              {q.solved ? <QuipSolved text={cfg?.quip_solved_text || SOLVED_TEXT} email={cfg?.m_email || "m@333sos.com"} />
                : <div className="mt-auto"><QuipKeys used={used} onKey={press} /></div>}
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}
